import { Component, DestroyRef, inject } from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { merge, Observable, take } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { VexConfigService } from '@vex/config/vex-config.service';
import { VexSidebarComponent } from '@vex/components/vex-sidebar/vex-sidebar.component';
import { AsyncPipe } from '@angular/common';
import { SidenavComponent } from '../components/sidenav/sidenav.component';
import { ToolbarComponent } from '../components/toolbar/toolbar.component';

import { ConfigPanelComponent } from '../components/config-panel/config-panel.component';
import { MatDialogModule } from '@angular/material/dialog';
import { BaseLayoutComponent } from '../base-layout/base-layout.component';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { VexProgressBarComponent } from '@vex/components/vex-progress-bar/vex-progress-bar.component';
import { TaskPanelOldComponent } from "../../../pages/projects_old/project/profile/tasks-table/task-panel/task-panel.component";
import { ConfigPanelService } from './config-panel.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'vex-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [
    BaseLayoutComponent,
    AsyncPipe,
    SidenavComponent,
    ToolbarComponent,
    VexSidebarComponent,
    ConfigPanelComponent,
    MatDialogModule,
    MatSidenavModule,
    RouterOutlet,
    VexProgressBarComponent,
    TaskPanelOldComponent
  ],
  standalone: true
})
export class LayoutComponent {
  isDesktop$: Observable<boolean> = this.layoutService.isDesktop$;
  sidenavCollapsed$: Observable<boolean> = this.layoutService.sidenavCollapsed$;
  sidenavDisableClose$: Observable<boolean> = this.layoutService.isDesktop$;
  sidenavFixedInViewport$: Observable<boolean> =
    this.layoutService.isDesktop$.pipe(map((isDesktop) => !isDesktop));
  sidenavMode$: Observable<MatDrawerMode> = this.layoutService.isDesktop$.pipe(
    map((isDesktop) => (isDesktop ? 'side' : 'over'))
  );
  sidenavOpen$: Observable<boolean> = this.layoutService.sidenavOpen$;
  configPanelOpen$: Observable<boolean> = this.configPanelService.configPanelOpen$;
  // configPanelOpen$: Observable<boolean> = this.layoutService.configPanelOpen$;
  taskPanelOpen$: Observable<boolean> = this.layoutService.taskPanelOpen$;


  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private sidenavWasCollapsed = false;
  private inReview = false;

  constructor(
    private readonly layoutService: VexLayoutService,
    private readonly configService: VexConfigService,
    private configPanelService: ConfigPanelService,
    private readonly router: Router
  ) {
    merge(
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) => event.urlAfterRedirects)
      ),
      this.configService.config$.pipe(map(() => this.router.url))
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((url) => this.syncSidenavToRoute(url));
  }

  private syncSidenavToRoute(url: string): void {
    const isReview = url.split('?')[0].startsWith('/review/');

    if (isReview) {
      if (!this.inReview) {
        this.inReview = true;
        this.layoutService.sidenavCollapsed$
          .pipe(take(1))
          .subscribe((collapsed) => (this.sidenavWasCollapsed = collapsed));
      }

      this.layoutService.collapseSidenav();

      return;
    }

    if (this.inReview) {
      this.inReview = false;

      if (!this.sidenavWasCollapsed) {
        this.layoutService.expandSidenav();
      }
    }
  }

  onConfigPanelOpenedChange(isOpened: boolean): void {
    if (isOpened) {
      this.configPanelService.openConfigPanel();
    } else {
      this.configPanelService.closeConfigPanel();
    }
  }

  onSidenavClosed(): void {
    this.layoutService.closeSidenav();
  }

  onSidebarClose() {
    this.configPanelService.closeConfigPanel();
  }

  onTaskPanelClosed(): void {
    this.layoutService.closeTaskPanel();
  }
}
